import { getApiErrorMessage } from "@/core/utils/apiError"

export interface ContentErrorShape {
  status: number
  code?: string
  error?: string
  data?: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

// RTK при не-JSON ответе прячет HTTP-статус в originalStatus.
// Технический error парсера/транспорта не является сообщением бэкенда.
export const normalizeContentError = (reason: unknown): ContentErrorShape => {
  if (!isRecord(reason)) return { status: 0 }
  const numericStatus = typeof reason.status === "number"
  const status =
    typeof reason.status === "number"
      ? reason.status
      : reason.status === "PARSING_ERROR" &&
          typeof reason.originalStatus === "number"
        ? reason.originalStatus
        : 0
  const data = isRecord(reason.data) ? reason.data : undefined
  return {
    status,
    code:
      typeof reason.code === "string"
        ? reason.code
        : typeof data?.code === "string"
          ? data.code
          : undefined,
    error:
      numericStatus && typeof reason.error === "string"
        ? reason.error
        : undefined,
    data,
  }
}

// Ветвление по коду (стабильные machine-readable коды §6 бэкенд-дока),
// затем по HTTP-статусу — НИКОГДА по тексту error (§6.4).
const CODE_MESSAGES: Record<string, string> = {
  empty_content: "Добавьте текст, файл или кнопку",
  invalid_reply_markup:
    "Проверьте кнопки: текст 1–64 символа, ссылка http(s) до 256 символов, не более 8 кнопок в строке и 100 всего",
  unknown_assets:
    "Некоторые выбранные файлы больше не существуют — обновите выбор существующих файлов",
  unknown_campaigns:
    "Кампания/ордер больше не существуют — обновите выбор владельцев",
  unknown_orders:
    "Кампания/ордер больше не существуют — обновите выбор владельцев",
  content_not_found: "Материал не найден — обновите список",
  binding_not_found: "Привязка уже снята",
  asset_not_found: "Файл не найден",
  campaign_not_found: "Объект не найден, обновите данные и повторите",
  order_not_found: "Объект не найден, обновите данные и повторите",
  aborted: "Отправка отменена. Результат неизвестен — обновите список",
}

// 408/500/504 — мутиционные формулировки («результат неизвестен», авторетрай
// запрещён); GET-списки этими сообщениями не пользуются: ошибки GET обрабатывает
// собственное состояние страницы/диалога с кнопкой «Повторить» (refetch, §6.4).
const STATUS_MESSAGES: Record<number, string> = {
  400: "Проверьте поля формы, фильтры, даты, количество и размеры файлов",
  401: "Сессия истекла, войдите заново",
  403: "Недостаточно прав",
  408: "Результат неизвестен — проверьте список перед повторной отправкой",
  413: "Уменьшите размер файлов, текста или общую загрузку",
  415: "Выберите поддержанный формат файла",
  500: "Результат неизвестен — проверьте список перед повторной отправкой",
  503: "Хранилище материалов недоступно; сохранение текста и кнопок остаётся доступным",
  504: "Результат неизвестен — проверьте список перед повторной отправкой",
}

export const contentErrorMessage = (reason: unknown): string => {
  const err = normalizeContentError(reason)
  const data = (err.data ?? null) as { error?: unknown; code?: unknown } | null
  const code =
    err.code ?? (typeof data?.code === "string" ? data.code : undefined)
  const text =
    err.error ?? (typeof data?.error === "string" ? data.error : undefined)
  if (code !== undefined && CODE_MESSAGES[code] !== undefined) {
    return CODE_MESSAGES[code]
  }
  if (err.status === 0) {
    return "Нет соединения с сервером"
  }
  const statusMessage = STATUS_MESSAGES[err.status]
  if (statusMessage !== undefined) {
    return err.status === 400 && text !== undefined
      ? `${statusMessage}: ${text}`
      : statusMessage
  }
  if (text !== undefined) {
    return text
  }
  return getApiErrorMessage(err)
}

// GET ссылки не использует мутационные сообщения «результат неизвестен».
export const assetLinkErrorMessage = (error: unknown): string => {
  const { status, code } = normalizeContentError(error)
  if (status === 404 && code === "asset_not_found") return "Файл не найден"
  if (status === 404 && code === "content_not_found")
    return "Материал не найден — обновите список"
  if (status === 401) return "Сессия истекла, войдите заново"
  if (status === 403) return "Недостаточно прав для загрузки файла"
  if (status === 503)
    return "Хранилище материалов недоступно — повторите загрузку позже"
  if (status >= 500)
    return "Не удалось загрузить файл с сервера — повторите позже"
  return "Не удалось загрузить файл — повторите попытку"
}
